import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {InfluencerGroup} from '../../../../models/influencer-group';
import {GroupService} from '../../../services/group-details/group.service';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user.service';
import {AchooPopupComponent} from '../../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../../services/modal/modal.service';
import {ObjectUtils} from '../../../utils/object.utils';
import {ValidationHandlerService} from '../../../core/validation-handler.service';
import {ErrorTemplate} from '../../../../models/ErrorTemplate';
import {CommonConstant, InfluencerGroupStatus} from '../../../app.constants';
import {PlaceholderVisibilityService} from '../../../services/placeholder-visibility/placeholder-visibility.service';
import {
  ActionDetails,
  ActionSource,
  ActivePages,
  EventTrackerService,
  Page,
  TargetType,
  UserActionType
} from '../../../services/event-tracker/event-tracker.service';
import {CacheService} from '../../../services/cache/cache.service';
import {Router} from '@angular/router';
import {GroupVersion, NewInfluencerGroup} from '../../../../models/auto-group/auto-group-setting';
import {InfluencerGroupsService} from '../../influencer-groups/influencer-groups.service';
import {NewGroupSettingsComponent} from '../new-group-settings/new-group-settings.component';
import {Subscription} from 'rxjs/Subscription';
import {ProjectService} from '../../../services/project/project.service';

@Component({
  selector: 'group-details-breadcumb',
  templateUrl: './group-details-breadcumb.component.html',
  styleUrls: ['./group-details-breadcumb.component.scss']
})
export class GroupDetailsBreadcumbComponent implements OnInit, OnDestroy {

  static readonly ONRELOAD = 'reload';

  @ViewChild('SaveGroupSettingError')
  saveGroupSettingError: AchooPopupComponent;
  @ViewChild('NewGroupSetting')
  newGroupSetting: NewGroupSettingsComponent;

  @Input()
  public influencerGroup: InfluencerGroup = new InfluencerGroup();
  // using for group setting support for both v1 and v2
  @Input()
  public groupSetting: InfluencerGroup | NewInfluencerGroup;
  @Input()
  public totalResult: number;
  @Input()
  public searchString: string;

  @Output()
  public searchTextEvent = new EventEmitter<string>();
  @Output()
  public updateGroupEvent = new EventEmitter<any>();
  @Output() editOrCreateProjectEvent: EventEmitter<any> = new EventEmitter<any>();

  public showGroupSetting = false;
  public updateGroupServerError: string;
  public listProjectOfAdvertiser: any  = [];
  // using to watch the event after creating or updating project
  private createOrUpdateProjectSubscription: Subscription;

  constructor(private groupService: GroupService,
              private userService: UserService,
              private cacheService: CacheService,
              private route: Router,
              private translate: TranslateService,
              private modalService: ModalService,
              private validationHandlerService: ValidationHandlerService,
              private placeholderVisibilityService: PlaceholderVisibilityService,
              private eventTracker: EventTrackerService,
              private projectService: ProjectService) {
  }

  public elementPaths = [];

  ngOnInit() {

    this.translate.get('group_details.breadcumb_root').subscribe((value) => {
      this.elementPaths.push({
        name: value,
        link: '/groups'
      });
    });
    this.elementPaths.push({name: this.influencerGroup.influencer_group_name,
      link: '/groups/' + this.influencerGroup.influencer_group_id});

    this.getAllProjectOfAdvertiser();

    this.createOrUpdateProjectSubscription = this.projectService.createOrUpdateProjectObserver.subscribe((project) => {
      if (project && this.newGroupSetting) {
        this.newGroupSetting.reloadAndSetSelectedProject(project);
      }
    });
  }

  ngOnDestroy() {
    if (this.createOrUpdateProjectSubscription) {
      this.createOrUpdateProjectSubscription.unsubscribe();
    }
  }

  getAllProjectOfAdvertiser() {
    this.userService.getAllProjectOfAdvertiser().subscribe((res: any) => {
      this.listProjectOfAdvertiser = res ? res.data : [];
    });
  }

  editInfluencerGroupSetting() {
    this.showGroupSetting = this.influencerGroup.status === InfluencerGroupStatus.PENDING ? false : true;
  }

  closeSetting() {
    this.showGroupSetting = false;
    setTimeout(() => {
      this.reloadGroupSettingData(this.influencerGroup.influencer_group_id);
    }, 100);
  }

  private reloadGroupSettingData(groupId) {
    this.groupSetting = null;
    this.groupService.getInfluencerGroup(groupId).subscribe((res: any) => {
      this.groupSetting = res;
    });
  }

  onSearchText(e) {
    this.searchTextEvent.emit(e);
  }

  closeWarningUpdateGroupPopup() {
    this.modalService.close();
  }

  getFirstLink(): string {
    return this.elementPaths.length > 0 ? this.elementPaths[0].link : '';
  }

  shouldShowArrowRight(index): boolean {
    return index < (this.elementPaths.length - 1);
  }

  shouldShowEditIcon(index): boolean {
    return index === (this.elementPaths.length - 1) && !this.showGroupSetting;
  }

  shouldShowCloseSettingIcon(index): boolean {
    return index === (this.elementPaths.length - 1) && this.showGroupSetting;
  }

  get activeAccount() {
    return this.userService.getCompanyId();
  }

  get openNewGroupSetting() {
    return this.groupSetting;
  }

  back() {
    this.cacheService.disableCacheRemoving(this.listGroupCachingKey(this.influencerGroup.influencer_group_id));
    this.route.onSameUrlNavigation = GroupDetailsBreadcumbComponent.ONRELOAD;
    this.route.navigate(['groups'], {
      queryParams: {
        activeAccount: this.activeAccount
      }
    });
  }

  private listGroupCachingKey(influencerGroupId) {
    return [
      `influencer_list_of_group_${influencerGroupId}`,
      `influencer_group_${influencerGroupId}`,
      `influencer_list_of_group_${influencerGroupId}_scrollId`,
    ];
  }

  get invalidActionData() {
    return {
      action: UserActionType.ACTION,
      item: Page.GROUP,
      target: {
        type: TargetType.INFLUENCER_GROUP
      },
      params: {
        action: UserActionType.UPDATE,
        source: ActionSource.PAGE,
        actionDetails: ActionDetails.UPDATE_CONFIG,
      },
      activePage: {
        type: ActivePages.GROUP_DETAILS,
      }
    };
  }

  updateGroup(evt) {
    this.updateGroupEvent.emit(this.influencerGroup);
  }

  editOrCreateProject(project) {
    this.editOrCreateProjectEvent.emit(project);
  }

  beforeSubmitGroupSetting() {
    this.showGroupSetting = false;
  }

  afterSubmitGroupSetting(evt) {
    if (evt.isError) {
      this.showGroupSetting = true;
      this.modalService.open(this.saveGroupSettingError, null, true);
    } else {
      this.reloadGroupSettingData(this.influencerGroup.influencer_group_id);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/main/group-details/group-details-breadcumb/group-details-breadcumb.component.ts